import { Component, computed, inject, signal } from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter, map } from 'rxjs';
import { AuthService } from './core/services/auth.service';
import { FloatingChat } from './shared/components/floating-chat/floating-chat';
import { ToastContainer } from './shared/components/toast-container/toast-container';
import { NotifyDialog } from './shared/components/notify-dialog/notify-dialog';
import { LegalConsentDialog } from './shared/components/legal-consent-dialog/legal-consent-dialog';
import { ForceChangePasswordModal } from './shared/components/force-change-password-modal/force-change-password-modal';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, FloatingChat, ToastContainer, NotifyDialog, LegalConsentDialog, ForceChangePasswordModal],
  template: `
    <router-outlet />
    <app-toast-container />
    <app-notify-dialog />
    @if (showChat()) {
      <app-floating-chat />
    }
    <!-- Consent popup ระดับ app — เด้งทันทีที่ login ไม่ว่าอยู่หน้าไหน (รวมหน้า public) -->
    <app-legal-consent-dialog [visible]="showConsentDialog()"
                              [loading]="consentLoading()"
                              (accepted)="onConsentAccepted()"
                              (rejected)="onConsentRejected()" />
    <!-- Force change password popup — เด้งเมื่อ user ที่ถูกสร้างใหม่ login เข้ามา -->
    @if (showForcePassword()) {
      <app-force-change-password-modal (changed)="onPasswordChanged()" />
    }
  `,
})
export class App {
  protected readonly authSvc = inject(AuthService);
  private readonly router = inject(Router);
  private readonly http = inject(HttpClient);

  private readonly url = toSignal(
    this.router.events.pipe(
      filter(e => e instanceof NavigationEnd),
      map(e => (e as NavigationEnd).urlAfterRedirects),
    ),
    { initialValue: this.router.url },
  );

  protected readonly showChat = computed(() => {
    const path = this.url().split('?')[0];
    // ซ่อน floating-chat เฉพาะใน owner backoffice (/owner/...) — หน้า public /owners* ต้องเห็น
    if (path === '/owner' || path.startsWith('/owner/')) return false;
    return this.authSvc.isLoggedIn();
  });

  /**
   * แสดง popup consent ทันทีที่ login + ยังไม่ยอมรับ — blocking ทุกหน้า (ไม่รวม admin)
   * ยกเว้นหน้า legal (terms/privacy) เพื่อให้ user อ่านเอกสารได้ก่อนยอมรับ
   */
  protected readonly showConsentDialog = computed(() => {
    const u = this.authSvc.currentUser();
    if (!u || u.hasAcceptedTerms !== false) return false;
    const path = this.url().split('?')[0];
    // ไม่แสดงในหน้า legal (ให้ user อ่านเอกสาร) และหน้า admin (admin ใช้คนละ auth scheme — popup นี้ไม่เกี่ยว)
    if (path === '/terms-and-conditions' || path === '/privacy-policy') return false;
    if (path.startsWith('/admin')) return false;
    return true;
  });
  protected readonly consentLoading = signal(false);

  /**
   * แสดง force-change-password popup เมื่อ user มี changePasswordFirst=true
   * - ไม่แสดงในหน้า admin (ใช้ scheme คนละตัว)
   * - แสดงเฉพาะหลัง login (currentUser != null) และยังไม่ได้ accept consent
   *   (consent มาก่อน — เปลี่ยน password หลังยอมรับ Terms)
   */
  protected readonly showForcePassword = computed(() => {
    const u = this.authSvc.currentUser();
    if (!u || !u.changePasswordFirst) return false;
    const path = this.url().split('?')[0];
    if (path.startsWith('/admin')) return false;
    if (path === '/member/activate') return false;
    if (this.showConsentDialog()) return false; // consent ก่อน password
    return true;
  });

  /** เปลี่ยน password สำเร็จ → reload profile (flag กลายเป็น false → popup หาย) */
  protected onPasswordChanged(): void {
    this.authSvc.refreshFromToken();
  }

  /** ยอมรับ — บันทึก + refresh profile */
  protected onConsentAccepted(): void {
    if (this.consentLoading()) return;
    this.consentLoading.set(true);
    this.http.post<{ success: boolean }>('/api/user/legal/accept', {}).subscribe({
      next: () => { this.consentLoading.set(false); this.authSvc.refreshFromToken(); },
      error: () => { this.consentLoading.set(false); },
    });
  }

  /** ไม่ยอมรับ — soft-delete ข้อมูล backend + logout */
  protected onConsentRejected(): void {
    if (this.consentLoading()) return;
    this.consentLoading.set(true);
    this.http.post<{ success: boolean }>('/api/user/legal/reject', {}).subscribe({
      next: () => { this.consentLoading.set(false); this.authSvc.signout(); },
      error: () => { this.consentLoading.set(false); this.authSvc.signout(); }, // logout ต่อแม้ API ล้ม
    });
  }
}
